import * as Sentry from '@sentry/react';

/**
 * Helper functions for working with Sentry
 */

/**
 * Manually capture an exception
 * @param error - The error to capture
 * @param context - Additional context to attach
 */
export const captureException = (error: Error, context?: Record<string, unknown>): void => {
    if (context) {
        Sentry.withScope((scope) => {
            Object.entries(context).forEach(([key, value]) => {
                scope.setContext(key, value as Sentry.Context | null);
            });
            Sentry.captureException(error);
        });
    } else {
        Sentry.captureException(error);
    }
};

/**
 * Capture a message (non-error event)
 * @param message - The message to capture
 * @param level - The severity level
 */
export const captureMessage = (
    message: string,
    level: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug' = 'info'
): void => {
    Sentry.captureMessage(message, level);
};

/**
 * Add a breadcrumb for debugging
 * @param message - Breadcrumb message
 * @param category - Category (e.g., 'auth', 'api', 'navigation')
 * @param data - Additional data
 */
export const addBreadcrumb = (
    message: string,
    category: string,
    data?: Record<string, unknown>
): void => {
    Sentry.addBreadcrumb({
        message,
        category,
        level: 'info',
        data,
    });
};

/**
 * Set user context for error tracking
 * @param user - User information
 */
export const setUser = (user: {
    id?: string;
    email?: string;
    username?: string;
    [key: string]: unknown;
} | null): void => {
    Sentry.setUser(user);
};

/**
 * Set custom tags for filtering in Sentry
 * @param tags - Key-value pairs of tags
 */
export const setTags = (tags: Record<string, string | number | boolean>): void => {
    Object.entries(tags).forEach(([key, value]) => {
        Sentry.setTag(key, value);
    });
};

/**
 * Set custom context for errors
 * @param name - Context name
 * @param context - Context data
 */
export const setContext = (name: string, context: Record<string, unknown>): void => {
    Sentry.setContext(name, context);
};

/**
 * Example usage in your components:
 * 
 * ```typescript
 * import { captureException, addBreadcrumb, setUser } from 'src/utils/sentry-helpers';
 * 
 * // When user logs in
 * setUser({ 
 *   id: userData.id, 
 *   email: userData.email,
 *   roleId: userData.roleId 
 * });
 * 
 * // Add breadcrumbs for debugging
 * addBreadcrumb('User navigated to dashboard', 'navigation');
 * 
 * // Capture errors with context
 * try {
 *   await fetchData();
 * } catch (error) {
 *   captureException(error as Error, { 
 *     component: 'Dashboard',
 *     action: 'fetchData',
 *     companyId: selectedCompanyId
 *   });
 * }
 * ```
 */
